import { InfoIcon } from '@/assets/icons/info-icons/info';
import { BannerDivider, Description, IconWrapper, StatusBannerWrapper, StatusWrapper } from '@/components/basicComponents/banner';
import { ErrorText } from '@/components/basicComponents/input';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { updateWizardSteps, WizardProgressBar } from '@/components/basicComponents/wizard/progressBar';
import Label from '@/components/basicComponents/label';
import FooterModal from '@/components/settings-page/importData/footer';
import { TwoColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useResponsive } from '@/hooks/use-responsive';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { ManagerSpotlightWizardStepWrapper } from '..';
import { AddButton } from '../../modify/addButton';
import { DeleteDynamicItem, DynamicSelectsWrapper, DynamicSelectWrapper } from '../../modify/radioWithDynamicSelects';
import { IListData } from '../../modify/types';
import { addStrategies, delStrategies, onChangeStrategies } from '../../modify/utils';

interface StrategiesStepProps {
  managerSpotlightWizardSteps: { name: string; status: string }[];
  strategies: IListData;
  setStrategies: Dispatch<SetStateAction<IListData>>;
  handleInvestmentIds: (chosenStrategies: string[]) => void;
  handleManagerSpotlightWizardSteps: (steps: { name: string; status: string }[]) => void;
}

export const StrategiesStep = ({
  managerSpotlightWizardSteps,
  strategies,
  setStrategies,
  handleInvestmentIds,
  handleManagerSpotlightWizardSteps
}: StrategiesStepProps) => {
  const theme = useTheme();
  const { isMobile } = useResponsive();

  const [errorMessage, setErrorMessage] = useState<string>(null);

  useEffect(() => {
    if (errorMessage && strategies.chosen.length && !strategies.chosen.includes('')) {
      setErrorMessage(null);
    }

    setStrategies((prevStrategies) => {
      return {
        ...prevStrategies,
        list: prevStrategies.list.filter((strategy) => !strategies.chosen.includes(strategy)).sort((a: string, b: string) => a.localeCompare(b))
      };
    });
  }, [strategies.chosen]);

  const prevStep = () => {
    updateWizardSteps('prev', managerSpotlightWizardSteps, handleManagerSpotlightWizardSteps);
  };

  const saveAndContinue = () => {
    if (!strategies.chosen.length || strategies.chosen.includes('')) {
      setErrorMessage('At least one strategy must be selected to continue');
      return;
    }

    handleInvestmentIds(strategies.chosen);
    updateWizardSteps('next', managerSpotlightWizardSteps, handleManagerSpotlightWizardSteps);
  };

  return (
    <>
      <ManagerSpotlightWizardStepWrapper isMobile={isMobile}>
        <WizardProgressBar steps={managerSpotlightWizardSteps} />
        <StrategiesStepWrapper>
          <StatusBannerWrapper bgColor={theme.layer[12]} withBorderRadius>
            <IconWrapper>
              <InfoIcon width={26} height={26} fill={theme.font.base} />
            </IconWrapper>
            <BannerDivider dividerColor={theme.font.disabled} />
            <StatusWrapper>
              <Description color={theme.font.base}>Select the strategies to include in the Manager Spotlight Email</Description>
            </StatusWrapper>
          </StatusBannerWrapper>
          <div>
            <Label size="md">Add Strategies</Label>
            <ErrorText size="md">{errorMessage}</ErrorText>
            <TwoColumnWrapper isMobile={isMobile}>
              <DynamicSelectsWrapper>
                {strategies.chosen.map((el, index) => (
                  <DynamicSelectWrapper key={`advisoryFirm-${index}`}>
                    <Select
                      value={strategies.chosen[index]}
                      previewValue={strategies.chosen[index]}
                      setValue={(value) => onChangeStrategies(value, index, strategies, setStrategies)}
                      options={strategies.list.map((item) => createSimpleSelectOption(item))}
                      styleType={strategies.chosen[strategies.chosen.length - 1] !== el ? 'disabled' : 'default'}
                      size="md"
                      withSearch
                    />
                    <DeleteDynamicItem onClick={() => delStrategies(el, index, strategies, setStrategies)}>
                      <img src="/static/icons/blue-cross.svg" alt="cross" />
                    </DeleteDynamicItem>
                  </DynamicSelectWrapper>
                ))}
                {strategies.chosen.length < 10 && strategies.list.length && strategies.chosen[strategies.chosen.length - 1] !== '' ? (
                  <AddButton text="Add" onClick={() => addStrategies(strategies, setStrategies)} />
                ) : (
                  <></>
                )}
                {strategies.chosen.length === 10 && <ErrorText size="md"> You have added the maximum number of strategies</ErrorText>}
              </DynamicSelectsWrapper>
            </TwoColumnWrapper>
          </div>
        </StrategiesStepWrapper>
      </ManagerSpotlightWizardStepWrapper>
      <FooterModal
        startOver={prevStep}
        startOverButtonName="Back"
        confirmButton={{
          name: 'Save And Continue',
          onClick: saveAndContinue,
          disabled: !!errorMessage
        }}
      />
    </>
  );
};

const StrategiesStepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
